import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetLotAnimalEbvsLazyQuery } from '@/publicSearch/graphql/queries.generated';
import { useStartSearchOnMount } from '../useStartSearchOnMount';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import { RegisteredBadgeStatus } from '@/components/publicSearch/searchSection/SaleCatalogueDetailAnimalCard';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import BreedAverageGridRowLabel from '@/components/publicSearch/animal/ebv/BreedAverageGridRowLabel';
import {
  resolveGridAvgRowCaptions,
  shouldStackBlackRedBreedAverages,
} from '@/components/publicSearch/animal/ebv/breedAverageLabels';
import { useEbvTraitExplanations } from '@/components/publicSearch/animal/ebv/useEbvTraitExplanations';
import { useSearchType } from '../useSearchType';
import {
  FieldOption,
  FieldsWithLink,
  GetAnimalsRow,
  GetSaleLotsRow,
  SaleLotsDefaultFieldOrder,
  SortDirection,
} from '@/components/publicSearch/constants';
import { getGridOperatorsByField } from '@/components/publicSearch/search/helpers';
import { OptionsByField } from '@/components/publicSearch/search/constants';
import { trimString } from '@/utils/text';
import { isFilterInputIncompleteForQuery } from '@/components/publicSearch/search/searchFilterEbvExpansion';
import { SocietyFormattedDate } from '@/components/publicSearch/SocietyFormattedDate';
import { pickPrimaryImageUrl } from '@/publicSearch/utils/pickPrimaryImageUrl';
import ImageLightbox from '@/components/publicSearch/ImageLightbox';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';
import { AnimalSexBadge, createRenderCell } from '../animal/helpers';
import {
  AnimalSearchFieldConfigItem,
  mapSettingFieldToRowKey,
  parseAnimalSearchFieldsConfig,
} from '../animal/animalEbvGrid';
import { SaleLotListRow } from '@/hooks/publicSearch/sale-catalogue/helpers';
import { buildLotAnimalFilterGroups } from '@/hooks/publicSearch/sale-catalogue/buildLotAnimalFilterGroups';
import { isUnmappedI6LookupError } from '@/components/publicSearch/search/expandI6LookupFilters';
import {
  resolveSaleLotDisplayName,
  resolveSaleLotRowId,
} from '@/hooks/publicSearch/sale-catalogue/resolveSaleLotDisplayName';
import { formatLotPrice } from '@/lib/formatLotPrice';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import {
  EbvAverageData,
  EbvConfigItem,
  createAverageRow,
  generateEbvColumns,
  generateEbvColumnGroupingModel,
  ebvGroupingOptionsFromDisplay,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { useRouter } from 'next/navigation';
import { logError } from '@/lib/logger';
import { Box } from '@/primitives';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { Box as MuiBox, CircularProgress } from '@mui/material';
import type {
  FilterGroupOfLotAnimalEbvFilteredFilterOptionInput,
  LotAnimalEbvFilteredSortOptionInput,
  LotAnimalEbvSortField,
  SortDirectionOrder,
} from '@/graphqlClient/types';

function DocumentImageCell({
  url,
  societyId,
  animalName,
}: {
  url: string | null;
  societyId: string | number;
  animalName?: string;
}) {
  const { imgSrc, isLoading, error } = useDocumentImageWithSociety(
    url,
    societyId,
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  if (!url) {
    return null;
  }

  if (error || (!isLoading && !imgSrc)) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 80,
          margin: 1,
          aspectRatio: '4/3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!imgSrc) {
    return null;
  }

  return (
    <>
      <MuiBox
        component="button"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsLightboxOpen(true);
        }}
        aria-label="View full-size image"
        sx={{
          width: '100%',
          maxWidth: 80,
          margin: 1,
          padding: 0,
          aspectRatio: '4/3',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'block',
          transition: (theme) =>
            theme.transitions.create(
              ['border-color', 'box-shadow', 'transform'],
              { duration: theme.transitions.duration.shorter },
            ),
          '&:hover': {
            boxShadow: 1,
            transform: 'translateY(-2px)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <MuiBox
          component="img"
          src={imgSrc}
          alt=""
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </MuiBox>
      <ImageLightbox
        open={isLightboxOpen}
        src={imgSrc}
        alt={animalName || 'Animal image'}
        title={animalName || undefined}
        onClose={() => setIsLightboxOpen(false)}
      />
    </>
  );
}

const LOT_ASKING_PRICE_FIELD = 'ASKING_PRICE';
const LOT_SALE_PRICE_FIELD = 'SALE_PRICE';
const LOT_NOTES_FIELD = 'LOT_NOTES';

function renderLotPriceCell(row: GetSaleLotsRow, kind: 'asking' | 'sale') {
  const rowAny = row as Record<string, unknown>;
  if (kind === 'asking') {
    const label = formatLotPrice(
      rowAny.askingPrice as number | null | undefined,
      rowAny.askingPriceDesc as string | null | undefined,
    );
    return label || '—';
  }
  const label = formatLotPrice(
    rowAny.salePrice as number | null | undefined,
    rowAny.salePriceDesc as string | null | undefined,
  );
  return label || '—';
}

function renderLotNotesCell(row: GetSaleLotsRow) {
  const notes = String((row as Record<string, unknown>).lotNotes ?? '').trim();
  if (!notes) return '—';
  const plain = notes.replace(/[<>]/g, '').trim();
  if (plain.length <= 60) return plain;
  return `${plain.slice(0, 57)}…`;
}

export function useSaleCatalogueDetailAnimalsFilter(
  { saleId, lotNo }: { saleId: string | null; lotNo?: string | null },
  enabled: boolean,
) {
  const router = useRouter();
  const orgCode = usePublicSearchContext(
    (context) => context.society.societyCode!,
  );
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();
  const {
    ebvLabel,
    showAccuracy,
    showBreedAverages,
    showPercentileSearch,
    showBreedplanHelp,
    isBlackRedAngus,
    useColourSlotGrouping,
  } = useEbvDisplayOptions();
  const { traitHeaderContext, popoverElement: ebvTraitExplanationPopover } =
    useEbvTraitExplanations(society.settings, showBreedplanHelp);
  const societyId = society.societyId ?? society.orgId ?? '';
  const searchType = useSearchType();
  const { commonFilterOperator, filters } = useFilterStore(searchType);
  const {
    gridSortModel,
    paginationModel,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
  } = useGridStore();
  const { resultDisplay } = useAnimalDisplayType();
  const isCardView = resultDisplay === RESULT_DISPLAY_TYPE.GALLERY;

  const [
    getSaleCatalogueDetailAnimals,
    { data, error, loading, previousData, refetch },
  ] = useGetLotAnimalEbvsLazyQuery();
  // See useAnimalEbvFilter for the rationale: route the *next* startSearch
  // through refetch() only when we have a tracked error, so the grid recovers
  // from a stuck ObservableQuery after errors / aborts without needing a
  // society-switch + apolloClient.resetStore().
  const recoveredFromErrorRef = useRef<unknown>(null);

  // See useSaleCatalogueFilter — Apollo's lazy-query handles can swap
  // reference between the render that fires the first execute and the
  // render that commits the response. Read them through refs so startSearch
  // is stable across the post-fetch render.
  const getSaleCatalogueDetailAnimalsRef = useRef<
    typeof getSaleCatalogueDetailAnimals
  >(getSaleCatalogueDetailAnimals);
  const refetchRef = useRef<typeof refetch>(refetch);
  const errorRef = useRef<typeof error>(error);
  // Keep latest-value refs in sync after each commit (read by the stable callback below).
  useEffect(() => {
    getSaleCatalogueDetailAnimalsRef.current = getSaleCatalogueDetailAnimals;
    refetchRef.current = refetch;
    errorRef.current = error;
  });

  // null = do not search. Never return [] on unmapped lookups — that would
  // drop the saleId base filter and fail open (unscoped lot query).
  const filterGroups = useMemo<
    FilterGroupOfLotAnimalEbvFilteredFilterOptionInput[] | null
  >(() => {
    if (!saleId) return null;
    try {
      return buildLotAnimalFilterGroups({
        saleId,
        lotNo,
        filters,
        commonFilterOperator,
        settings: society.settings,
      });
    } catch (error) {
      // ILRX-4434: skip search entirely (same as animals). Do not invent
      // empty groups — buildLotAnimalFilterGroups always scopes by saleId.
      if (isUnmappedI6LookupError(error)) {
        return null;
      }
      throw error;
    }
  }, [commonFilterOperator, filters, lotNo, saleId, society.settings]);

  const ebvConfig = useMemo<EbvConfigItem[]>(() => {
    if (!society.settings || !Array.isArray(society.settings)) return [];

    const ebvColumnsSetting = society.settings.find(
      (setting) =>
        setting &&
        setting.societySettingType === SOCIETY_SETTING_TYPE.EBV_FIELDS,
    );

    if (!ebvColumnsSetting || !ebvColumnsSetting.value) return [];

    try {
      const parsed = JSON.parse(ebvColumnsSetting.value) as EbvConfigItem[];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      logError('Failed to parse EBV columns configuration:', error);
      return [];
    }
  }, [society.settings]);

  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );

  // Parse ANIMAL_LIST_FIELDS from society settings to drive lot column
  // labels, order, and visibility (LOT_NO stays pinned at the start and
  // IMAGES at the end of the base columns).
  const animalSearchFieldsConfig = useMemo(
    () => parseAnimalSearchFieldsConfig(society.settings),
    [society.settings],
  );

  const currencySymbol = useCurrencySymbol(society);

  const ebvAverage = useMemo<EbvAverageData | null>(() => {
    if (!society.settings || !Array.isArray(society.settings)) return null;

    const ebvAverageSetting = society.settings.find(
      (setting) =>
        setting &&
        setting.societySettingType === SOCIETY_SETTING_TYPE.EBV_AVERAGE,
    );

    if (!ebvAverageSetting || !ebvAverageSetting.value) return null;

    try {
      const parsed = JSON.parse(ebvAverageSetting.value) as EbvAverageData;
      return parsed || null;
    } catch (error) {
      logError('Failed to parse EBV average configuration:', error);
      return null;
    }
  }, [society.settings]);

  const effectiveShowEBVAccuracy = showEBVAccuracy && showAccuracy;
  const effectiveShowPercentiles = showPercentiles && showPercentileSearch;
  const effectiveEbvAverage = showBreedAverages ? ebvAverage : null;

  const sortOptions = useMemo<LotAnimalEbvFilteredSortOptionInput[]>(() => {
    // Valid LotAnimalEbvSortField values for this grid (matches GraphQL enum).
    // Narrow list avoids sending list-view sorts (e.g. SALE_DATE) when opening detail.
    const validSortFields = new Set<string>([
      FieldOption.lotNo.value,
      FieldOption.animalName.value,
      FieldOption.animalIdent.value,
      FieldOption.dateOfBirth.value,
      FieldOption.registrationStatus.value,
      FieldOption.sex.value,
    ]);
    for (let i = 1; i <= 26; i++) {
      validSortFields.add(`EBV${i}`);
    }

    const options = gridSortModel
      .filter(({ field, sort }) => field && sort && validSortFields.has(field))
      .map(({ field, sort }) => {
        const f = field as string;
        const isEbvValueSort = /^EBV\d+$/.test(f);
        return {
          direction: SortDirection[sort!] as SortDirectionOrder,
          field: field as LotAnimalEbvSortField,
          nullsLast: isEbvValueSort,
        };
      });

    if (options.length > 0) return options;

    return [
      {
        field: FieldOption.lotNo.value as LotAnimalEbvSortField,
        direction: SortDirection.asc as SortDirectionOrder,
        nullsLast: false,
      },
    ];
  }, [gridSortModel]);

  const columns: GridColDef<GetSaleLotsRow>[] = useMemo(() => {
    // LOT_NO is lot-specific and always pinned first; not configurable
    // through ANIMAL_LIST_FIELDS.
    const lotNoColumn: GridColDef<GetSaleLotsRow> = {
      field: FieldOption.lotNo.value,
      filterable: false,
      headerName: FieldOption.lotNo.label,
      sortable: true,
      width: 88,
      minWidth: 72,
    };

    const askingPriceColumn: GridColDef<GetSaleLotsRow> = {
      field: LOT_ASKING_PRICE_FIELD,
      headerName: 'Asking Price',
      filterable: false,
      sortable: false,
      width: 120,
      minWidth: 100,
      renderCell: ({ row }) => renderLotPriceCell(row, 'asking'),
    };

    const salePriceColumn: GridColDef<GetSaleLotsRow> = {
      field: LOT_SALE_PRICE_FIELD,
      headerName: 'Sale Price',
      filterable: false,
      sortable: false,
      width: 120,
      minWidth: 100,
      renderCell: ({ row }) => renderLotPriceCell(row, 'sale'),
    };

    const lotNotesColumn: GridColDef<GetSaleLotsRow> = {
      field: LOT_NOTES_FIELD,
      headerName: 'Lot Notes',
      filterable: false,
      sortable: false,
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) => renderLotNotesCell(row),
    };

    // IMAGES is rendered from `documents` / `animalDocuments` and remains
    // pinned at the end of the base columns (before any EBV columns).
    const imagesColumn: GridColDef<GetSaleLotsRow> = {
      field: 'IMAGES',
      headerName: 'Images',
      width: 100,
      minWidth: 90,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const url = pickPrimaryImageUrl(
          row.documents as Parameters<typeof pickPrimaryImageUrl>[0],
          row.animalDocuments as Parameters<typeof pickPrimaryImageUrl>[1],
        );
        const animalName =
          displayPlainText(row.ANIMAL_NAME) || row.ANIMAL_IDENT || undefined;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <DocumentImageCell
              url={url}
              societyId={societyId}
              animalName={animalName}
            />
          </Box>
        );
      },
    };

    const buildAnimalColumn = (
      fieldKey: string,
      headerName: string,
    ): GridColDef<GetSaleLotsRow> | null => {
      if (!fieldKey) return null;

      // Lot-specific fixed columns can't be re-rendered via this builder.
      if (
        fieldKey === FieldOption.lotNo.value ||
        fieldKey === FieldOption.images.value
      ) {
        return null;
      }

      if (fieldKey === FieldOption.animalName.value) {
        return {
          field: fieldKey,
          filterOperators: getGridOperatorsByField(fieldKey),
          headerName,
          sortable: true,
          flex: 1,
          minWidth: 250,
          renderCell: (params: GridRenderCellParams<GetSaleLotsRow>) => {
            if (params.row.ID === -1) {
              if (effectiveEbvAverage) {
                return (
                  <BreedAverageGridRowLabel
                    ebvAverage={effectiveEbvAverage}
                    isBlackRedAngus={isBlackRedAngus}
                    showPercentileSearch={isEbvEnabled && showPercentileSearch}
                    orgCode={orgCode}
                    societyId={societyId}
                    onAverageRowClick={
                      isEbvEnabled && showPercentileSearch
                        ? () =>
                            router.push(
                              `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages`,
                            )
                        : undefined
                    }
                  />
                );
              }
              return <span>{params.row.ANIMAL_NAME || ''}</span>;
            }
            return <SaleLotListRow orgCode={orgCode} row={params.row} />;
          },
        };
      }

      if (fieldKey === FieldOption.sex.value) {
        return {
          field: fieldKey,
          filterOperators: getGridOperatorsByField(fieldKey),
          headerName,
          sortable: true,
          width: 105,
          renderCell: (params: GridRenderCellParams<GetSaleLotsRow>) => (
            <AnimalSexBadge sex={params.row.SEX ?? ''} />
          ),
        };
      }

      if (fieldKey === FieldOption.dateOfBirth.value) {
        return {
          field: fieldKey,
          filterOperators: getGridOperatorsByField(fieldKey),
          headerName,
          sortable: true,
          flex: 1,
          minWidth: 120,
          renderCell: ({ row }) => {
            const rawDate = row[fieldKey as keyof GetSaleLotsRow];
            if (!rawDate) return '';
            return <SocietyFormattedDate value={rawDate as string} asDob />;
          },
        };
      }

      if (fieldKey === FieldOption.registrationStatus.value) {
        return {
          field: fieldKey,
          filterOperators: getGridOperatorsByField(fieldKey),
          headerName,
          sortable: true,
          flex: 1,
          minWidth: 130,
          renderCell: ({ row }) => (
            <RegisteredBadgeStatus
              status={(row[fieldKey as keyof GetSaleLotsRow] as string) ?? ''}
            />
          ),
        };
      }

      // Default: reuse the animal-screen render cell, which already handles
      // ANIMAL_IDENT, BREEDER, CURRENT_OWNER, SIRE, DAM, SIRE_IDENT,
      // DAM_IDENT, ANIMAL_STATUS, etc.
      return {
        field: fieldKey,
        filterOperators: getGridOperatorsByField(fieldKey),
        headerName,
        sortable: true,
        flex: 1,
        minWidth: fieldKey === FieldOption.animalIdent.value ? 120 : 150,
        headerClassName: FieldsWithLink.includes(fieldKey) ? 'pl' : '',
        type: fieldKey in OptionsByField ? 'singleSelect' : undefined,
        valueOptions:
          fieldKey in OptionsByField
            ? () => OptionsByField[fieldKey]!.map(({ value }) => value)
            : undefined,
        renderCell: (params: GridRenderCellParams<GetSaleLotsRow>) =>
          createRenderCell(
            fieldKey as keyof GetAnimalsRow,
            orgCode,
          )(params as any),
      };
    };

    // Resolve animal-related columns: society setting wins, otherwise fall
    // back to the historical hard-coded layout so societies that haven't
    // configured ANIMAL_LIST_FIELDS see the same grid as before.
    const fallbackConfig: AnimalSearchFieldConfigItem[] =
      SaleLotsDefaultFieldOrder.filter(
        (item) =>
          item.value !== FieldOption.lotNo.value &&
          item.value !== FieldOption.images.value,
      ).map((item) => ({
        // SaleLotsDefaultFieldOrder uses uppercase row keys (ANIMAL_NAME, SEX, ...)
        // which already match the column field, so we use them directly and
        // skip mapSettingFieldToRowKey for the fallback path.
        field: item.value,
        // Preserve the slightly different historical labels for lots.
        label:
          {
            [FieldOption.animalName.value]: 'Name',
            [FieldOption.animalIdent.value]: 'Animal Ident',
            [FieldOption.dateOfBirth.value]: 'Birth Date',
            [FieldOption.registrationStatus.value]: 'Registered Status',
          }[item.value] ?? item.label,
        display: 1,
        order: '0',
      }));

    const useSocietyConfig = animalSearchFieldsConfig.length > 0;
    const configItems = useSocietyConfig
      ? animalSearchFieldsConfig
      : fallbackConfig;

    const animalColumns = configItems
      .map((item) => {
        const fieldKey = useSocietyConfig
          ? mapSettingFieldToRowKey(item.field)
          : item.field;
        const label = (item.label || '').replace(/\^/g, ' ');
        return buildAnimalColumn(fieldKey, label);
      })
      .filter((col): col is GridColDef<GetSaleLotsRow> => col !== null);

    const baseColumns: GridColDef<GetSaleLotsRow>[] = [
      lotNoColumn,
      askingPriceColumn,
      salePriceColumn,
      lotNotesColumn,
      ...animalColumns,
      imagesColumn,
    ];

    if (effectiveEbvConfig.length > 0) {
      const ebvColumns = generateEbvColumns(
        effectiveEbvConfig,
        showEBVs,
        effectiveShowEBVAccuracy,
        effectiveShowPercentiles,
        currencySymbol,
        ebvLabel,
        traitHeaderContext,
        {
          isBlackRedAngusStrain: isBlackRedAngus,
          ebvAverage: effectiveEbvAverage,
        },
      ) as GridColDef<GetSaleLotsRow>[];
      baseColumns.push(...ebvColumns);
    }

    return baseColumns;
  }, [
    animalSearchFieldsConfig,
    effectiveEbvConfig,
    orgCode,
    router,
    showEBVs,
    effectiveShowEBVAccuracy,
    effectiveShowPercentiles,
    currencySymbol,
    societyId,
    showPercentileSearch,
    ebvLabel,
    isEbvEnabled,
    traitHeaderContext,
    effectiveEbvAverage,
    isBlackRedAngus,
  ]);

  const columnGroupingModel = useMemo(() => {
    if (effectiveEbvConfig.length === 0) return [];
    return generateEbvColumnGroupingModel(
      effectiveEbvConfig,
      ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    );
  }, [effectiveEbvConfig, useColourSlotGrouping]);

  // Render rows from `data ?? previousData` rather than blanking the
  // grid on every `loading` flip. Same rationale as the other filter
  // hooks: MUI's loading overlay already covers the grid, so the manual
  // `if (loading) return []` only produced the visible "data being
  // rerendered" jank on every refetch (filter / sort / pagination /
  // EBV-toggle).
  const rows = useMemo(() => {
    const sourceData = data ?? previousData;
    const dataRows =
      sourceData?.lotAnimalEbvs?.items.map((item) => {
        const row = {
          ID: item.animalId ?? 0,
          LOT_ANIMAL_ID: item.lotAnimalId ?? undefined,
          LOT_NO: item.lotNo,
          ANIMAL_IDENT: item.animalIdent || '',
          // i4: COALESCE(lo.lot_anim_name, an.anim_name) — keeps "Herd Bull:" labels
          ANIMAL_NAME: resolveSaleLotDisplayName(
            item.lotAnimalName,
            item.animalName,
          ),
          ANIMAL_STATUS: item.animalStatus || '',
          DATE_OF_BIRTH: item.dateOfBirth || '',
          REGISTRATION_STATUS: trimString(item.registrationStatus),
          SEX: item.sex ?? '',
          ...item,
        } as GetSaleLotsRow & { [key: string]: unknown };

        // Re-apply after spread so camelCase animalName from the DTO cannot win.
        row.ANIMAL_NAME = resolveSaleLotDisplayName(
          item.lotAnimalName,
          item.animalName,
        );
        row.saleId = item.saleId;

        const itemAny = item as Record<string, unknown>;
        row.TRAIT_LEADER = item.traitLeader || '';
        row.TATTOO = (item.animalTattoo as string) || '';
        row.CALVING_YEAR = (item.calvingYear as string) || '';

        // LinkCell-shaped fields used by the animal createRenderCell for
        // BREEDER, CURRENT_OWNER, SIRE and DAM.
        if (item.breederId || item.breederName) {
          row.BREEDER = {
            id: item.breederId || 0,
            value: item.breederName || '',
            detailsAvailFlag: item.breederDetailsAvailFlag ?? null,
          };
        }
        if (item.currentOwnerId || item.currentOwnerName) {
          row.CURRENT_OWNER = {
            id: item.currentOwnerId || 0,
            value: item.currentOwnerName || '',
            detailsAvailFlag: item.currentOwnerDetailsAvailFlag ?? null,
          };
        }
        if (item.sireId || item.sireName) {
          row.SIRE = {
            id: item.sireId || 0,
            value: item.sireName || '',
          };
        }
        if (item.damId || item.damName) {
          row.DAM = {
            id: item.damId || 0,
            value: item.damName || '',
          };
        }

        if (itemAny.sireIdent != null && itemAny.sireIdent !== '') {
          row.SIRE_IDENT = itemAny.sireIdent;
        }
        if (itemAny.damIdent != null && itemAny.damIdent !== '') {
          row.DAM_IDENT = itemAny.damIdent;
        }
        if (itemAny.traitsObserved != null && itemAny.traitsObserved !== '') {
          row.TRAITS_OBSERVED = itemAny.traitsObserved;
        }

        // Map society-defined animal fields 1..30 to row keys consumed by
        // ANIMAL_LIST_FIELDS columns (e.g. SOCIETY_ANIMAL_FIELD_8).
        for (let i = 1; i <= 30; i++) {
          const key = `societyAnimalField${i}`;
          if (key in item && itemAny[key] != null && itemAny[key] !== '') {
            row[`SOCIETY_ANIMAL_FIELD_${i}`] = itemAny[key];
          }
        }

        for (let i = 1; i <= 26; i++) {
          const ebvKey = `ebv${i}` as keyof typeof item;
          const ebvAccKey = `ebv${i}Accuracy` as keyof typeof item;
          const ebvPercentileKey = `ebv${i}Percentile` as keyof typeof item;

          if (
            ebvKey in item &&
            item[ebvKey] !== null &&
            item[ebvKey] !== undefined
          ) {
            const ebvField = `EBV${i}`;
            row[ebvField] = item[ebvKey] || '';
          }

          if (
            ebvAccKey in item &&
            item[ebvAccKey] !== null &&
            item[ebvAccKey] !== undefined
          ) {
            const accField = `EBV${i}_ACC`;
            row[accField] = item[ebvAccKey] || '';
          }

          if (
            ebvPercentileKey in item &&
            item[ebvPercentileKey] !== null &&
            item[ebvPercentileKey] !== undefined
          ) {
            const percentileField = `EBV${i}_PERCENTILE`;
            row[percentileField] = item[ebvPercentileKey];
          }
        }

        return row;
      }) || [];

    const averageRowLabel = effectiveEbvAverage
      ? resolveGridAvgRowCaptions(
          shouldStackBlackRedBreedAverages(
            isBlackRedAngus,
            effectiveEbvAverage,
          ),
          effectiveEbvAverage,
          showPercentileSearch,
        )
      : undefined;

    const averageRow =
      !isCardView &&
      (showEBVs || effectiveShowEBVAccuracy || effectiveShowPercentiles) &&
      effectiveEbvAverage &&
      effectiveEbvConfig.length > 0
        ? (createAverageRow(
            effectiveEbvAverage,
            effectiveEbvConfig,
            currencySymbol,
            averageRowLabel,
            {
              isBlackRedAngusStrain: shouldStackBlackRedBreedAverages(
                isBlackRedAngus,
                effectiveEbvAverage,
              ),
            },
          ) as GetSaleLotsRow | null)
        : null;

    return averageRow ? [averageRow, ...dataRows] : dataRows;
  }, [
    data,
    previousData,
    effectiveEbvAverage,
    effectiveEbvConfig,
    isCardView,
    showEBVs,
    effectiveShowEBVAccuracy,
    effectiveShowPercentiles,
    currencySymbol,
    showPercentileSearch,
    isBlackRedAngus,
  ]);

  const startSearch = useCallback(() => {
    if (!enabled || !saleId || !orgId || !filterGroups) return;
    // NOTE: do not gate the entire sale-detail-animals query on isEbvEnabled.
    // EBV status only affects column rendering (effectiveEbvConfig); we still
    // need to fire GetLotAnimalEbvs for societies whose data has
    // isEbvEnabled=false, otherwise the sale-detail page is stuck with no
    // POST in the network tab.
    if (filters.some((f) => isFilterInputIncompleteForQuery(f))) {
      return;
    }
    const variables = {
      requestInput: {
        pagination: {
          currentPage: (paginationModel.page ?? 0) + 1,
          pageSize: paginationModel.pageSize,
        },
        filterGroups,
        sortOptions,
      },
    };
    const context = { headers: setDefaultHeader(orgId) };

    const currentError = errorRef.current;
    if (currentError && recoveredFromErrorRef.current !== currentError) {
      recoveredFromErrorRef.current = currentError;
      try {
        void refetchRef.current(variables);
      } catch {
        getSaleCatalogueDetailAnimalsRef.current({ variables, context });
      }
      return;
    }
    getSaleCatalogueDetailAnimalsRef.current({ variables, context });
  }, [
    enabled,
    filterGroups,
    filters,
    orgId,
    // See useMembersFilter — depend on primitives, not the full
    // paginationModel object, so PublicSearchInput's mount-time
    // setPaginationModel({page:0,pageSize:…}) doesn't invalidate
    // startSearch and cause a duplicate POST.
    paginationModel.page,
    paginationModel.pageSize,
    saleId,
    sortOptions,
  ]);

  // Content signature of the request variables — de-dupes redundant fires
  // (StrictMode + stale-then-fresh society.settings churn) in
  // useStartSearchOnMount while still re-firing on real filter/sort/page
  // changes. See useStartSearchOnMount for the full rationale.
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        filterGroups,
        sortOptions,
        orgId,
        saleId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [
      filterGroups,
      sortOptions,
      orgId,
      saleId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useStartSearchOnMount(startSearch, enabled, searchSignature);

  return useMemo(
    () => ({
      data: data?.lotAnimalEbvs,
      dataGrid: {
        columns,
        rows,
        columnGroupingModel,
        getRowId: (row: GetSaleLotsRow) => resolveSaleLotRowId(row),
      },
      done: (data || error) && !loading,
      error,
      loading,
      previousData: previousData?.lotAnimalEbvs,
      startSearch,
      ebvTraitExplanationPopover,
    }),
    [
      columns,
      columnGroupingModel,
      data,
      error,
      loading,
      previousData,
      rows,
      startSearch,
      ebvTraitExplanationPopover,
    ],
  );
}
